import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Check, X, ShieldCheck, Building2, Mail } from "lucide-react";
import OutreachTab from "../components/OutreachTab";

export default function AdminPage() {
  const { user, loading: authLoading } = useAuth() || {};
  const navigate = useNavigate();
  const [bizs, setBizs] = useState([]);
  const [revs, setRevs] = useState([]);
  const [claims, setClaims] = useState([]);
  const [activeTab, setActiveTab] = useState("moderation");

  useEffect(() => {
    if (authLoading) return;
    if (!user) navigate("/login");
    else if (!user.is_admin) navigate("/");
  }, [user, authLoading, navigate]);

  const load = useCallback(async () => {
    if (!user?.is_admin) return;
    const [b, r, c] = await Promise.all([
      api.get("/admin/businesses/pending"),
      api.get("/admin/reviews/pending"),
      api.get("/admin/claims/pending"),
    ]);
    setBizs(b.data);
    setRevs(r.data);
    setClaims(c.data);
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const act = async (type, id, action) => {
    await api.post(`/admin/${type}/${id}/${action}`);
    load();
  };

  if (!user?.is_admin) return null;

  return (
    <div className="min-h-screen bg-lokaso-sand py-10 px-4" data-testid="admin-page">
      <div className="max-w-6xl mx-auto">
        <h1 className="font-display text-3xl md:text-4xl font-bold flex items-center gap-2">
          <ShieldCheck className="text-lokaso-green" /> Administration
        </h1>
        <p className="text-stone-600 mt-1">Modération des entreprises et avis en attente.</p>

        {/* Tabs */}
        <div className="mt-6 flex gap-2 border-b border-stone-200" data-testid="admin-tabs">
          <button
            onClick={() => setActiveTab("moderation")}
            className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px ${activeTab === "moderation" ? "border-lokaso-green text-lokaso-green" : "border-transparent text-stone-500 hover:text-lokaso-dark"}`}
            data-testid="admin-tab-moderation"
          >
            🛡️ Modération
          </button>
          <button
            onClick={() => setActiveTab("outreach")}
            className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px inline-flex items-center gap-1.5 ${activeTab === "outreach" ? "border-lokaso-green text-lokaso-green" : "border-transparent text-stone-500 hover:text-lokaso-dark"}`}
            data-testid="admin-tab-outreach"
          >
            <Mail className="w-4 h-4" /> Outreach Email
          </button>
        </div>

        {activeTab === "outreach" && (
          <div className="mt-6"><OutreachTab /></div>
        )}

        {activeTab === "moderation" && (<>

        {/* Pending businesses */}
        <section className="mt-8 bg-white rounded-2xl border border-stone-200 overflow-hidden" data-testid="admin-pending-businesses">
          <div className="px-6 py-4 border-b border-stone-200">
            <h2 className="font-display text-xl font-bold">Entreprises en attente ({bizs.length})</h2>
          </div>
          {bizs.length === 0 ? (
            <div className="p-6 text-stone-500">Aucune entreprise en attente.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-lokaso-cream"><tr>
                <th className="text-left px-4 py-3">Nom</th>
                <th className="text-left px-4 py-3 hidden md:table-cell">Catégorie</th>
                <th className="text-left px-4 py-3 hidden md:table-cell">Ville</th>
                <th className="text-left px-4 py-3 hidden md:table-cell">Date</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr></thead>
              <tbody>
                {bizs.map((b) => (
                  <tr key={b.id} className="border-t border-stone-100">
                    <td className="px-4 py-3 font-semibold">
                      <div>{b.name}</div>
                      <div className="text-xs text-stone-500">{b.address}</div>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell capitalize">{b.category_slug}</td>
                    <td className="px-4 py-3 hidden md:table-cell capitalize">{b.city_slug}</td>
                    <td className="px-4 py-3 hidden md:table-cell text-stone-500">{new Date(b.created_at).toLocaleDateString("fr-FR")}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-2">
                        <button onClick={() => act("businesses", b.id, "approve")} className="px-3 py-1.5 rounded-lg bg-lokaso-green text-white text-xs font-semibold inline-flex items-center gap-1" data-testid={`admin-approve-biz-${b.id}`}>
                          <Check className="w-3.5 h-3.5" /> Approuver
                        </button>
                        <button onClick={() => act("businesses", b.id, "reject")} className="px-3 py-1.5 rounded-lg bg-lokaso-red text-white text-xs font-semibold inline-flex items-center gap-1" data-testid={`admin-reject-biz-${b.id}`}>
                          <X className="w-3.5 h-3.5" /> Rejeter
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        {/* Pending reviews */}
        <section className="mt-8 bg-white rounded-2xl border border-stone-200 overflow-hidden" data-testid="admin-pending-reviews">
          <div className="px-6 py-4 border-b border-stone-200">
            <h2 className="font-display text-xl font-bold">Avis en attente ({revs.length})</h2>
          </div>
          {revs.length === 0 ? (
            <div className="p-6 text-stone-500">Aucun avis en attente.</div>
          ) : (
            <ul className="divide-y divide-stone-100">
              {revs.map((r) => (
                <li key={r.id} className="p-4 md:p-6 flex flex-col md:flex-row gap-4">
                  <div className="flex-1">
                    <p className="font-semibold">{r.business?.name || "—"}</p>
                    <p className="text-xs text-stone-500">par {r.author_name} · {r.rating}★</p>
                    <p className="mt-2 text-sm">{r.body}</p>
                  </div>
                  <div className="flex gap-2 md:flex-col md:w-32">
                    <button onClick={() => act("reviews", r.id, "approve")} className="px-3 py-1.5 rounded-lg bg-lokaso-green text-white text-xs font-semibold inline-flex items-center justify-center gap-1" data-testid={`admin-approve-rev-${r.id}`}>
                      <Check className="w-3.5 h-3.5" /> Approuver
                    </button>
                    <button onClick={() => act("reviews", r.id, "reject")} className="px-3 py-1.5 rounded-lg bg-lokaso-red text-white text-xs font-semibold inline-flex items-center justify-center gap-1" data-testid={`admin-reject-rev-${r.id}`}>
                      <X className="w-3.5 h-3.5" /> Rejeter
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Pending claims */}
        <section className="mt-8 bg-white rounded-2xl border border-stone-200 overflow-hidden" data-testid="admin-pending-claims">
          <div className="px-6 py-4 border-b border-stone-200">
            <h2 className="font-display text-xl font-bold flex items-center gap-2">
              <Building2 className="w-5 h-5 text-lokaso-green" /> Réclamations de fiches ({claims.length})
            </h2>
          </div>
          {claims.length === 0 ? (
            <div className="p-6 text-stone-500">Aucune réclamation en attente.</div>
          ) : (
            <ul className="divide-y divide-stone-100">
              {claims.map((c) => (
                <li key={c.id} className="p-4 md:p-6 flex flex-col md:flex-row gap-4" data-testid={`admin-claim-${c.id}`}>
                  <div className="flex-1 text-sm">
                    <p className="font-semibold">{c.business_name}</p>
                    <p className="text-xs text-stone-500 mt-0.5">
                      Demandé par <strong>{c.full_name}</strong> · {c.email}
                      {c.phone && <> · {c.phone}</>}
                    </p>
                    {c.message && (
                      <p className="mt-2 text-stone-700 italic border-l-2 border-stone-200 pl-3">« {c.message} »</p>
                    )}
                    <p className="text-[11px] text-stone-400 mt-2">
                      Reçu le {new Date(c.created_at).toLocaleString("fr-FR")}
                    </p>
                  </div>
                  <div className="flex gap-2 md:flex-col md:w-32">
                    <button
                      onClick={() => act("claims", c.id, "approve")}
                      className="px-3 py-1.5 rounded-lg bg-lokaso-green text-white text-xs font-semibold inline-flex items-center justify-center gap-1"
                      data-testid={`admin-approve-claim-${c.id}`}
                    >
                      <Check className="w-3.5 h-3.5" /> Approuver
                    </button>
                    <button
                      onClick={() => act("claims", c.id, "reject")}
                      className="px-3 py-1.5 rounded-lg bg-lokaso-red text-white text-xs font-semibold inline-flex items-center justify-center gap-1"
                      data-testid={`admin-reject-claim-${c.id}`}
                    >
                      <X className="w-3.5 h-3.5" /> Rejeter
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
        </>)}
      </div>
    </div>
  );
}
